/**
 * MUI X Date Picker wrappers, drawn with the same MUI theme as the pickers in Select.jsx
 * (lib/muiTheme.js), so they follow light, dark and the palette with the rest.
 *
 * A bad date can be typed but is never passed on. The field is MUI's sectioned one: each
 * part only takes digits that part could ever hold — a month up to 12, a day up to 31, a
 * year 0000–9999 — but the parts are not checked against each other or against
 * minDate/maxDate while typing, so 2025-02-30 or a year outside the bounds can be keyed in.
 * Only a whole, valid date inside minDate/maxDate reaches `onChange`; anything else stays
 * in the box, marked as an error, and the page keeps the last good value. Values are
 * exchanged as ISO 'YYYY-MM-DD' strings, matching newsletters.published_on.
 */
import { useState } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useFieldDescription } from './fieldContext'

const toDate = (iso) => {
  if (!iso) return null
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** A day as YYYY-MM-DD in the reader's own time zone — not UTC, which is tomorrow by evening. */
export const toIso = (d) => {
  if (!d) return null
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Single date. `value`/`onChange` speak ISO strings.
 *
 * The field shows YYYY-MM-DD when empty — the sectioned field draws its own per-part
 * placeholders and has no free-text one — so `placeholder` is the field's accessible name
 * when no <Field htmlFor={id}> labels it, as it is for the pickers in Select.jsx.
 *
 * `label` draws MUI's own floating label inside the outline instead: the word sits where
 * YYYY-MM-DD would until the field is focused or filled. For a field with no <Field> of
 * its own to say what it is — the two halves of a range.
 */
export function DateField({
  value,
  onChange,
  placeholder = 'Pick a date',
  label,
  id,
  minDate,
  maxDate,
  isClearable = true,
  disabled,
}) {
  /* A date the page was not given — a To before the From, a year before 1900 — stays in
     the box as typed, marked as an error, rather than being passed on. MUI puts a
     controlled field back to its `value` when a whole date is typed and the value does
     not follow, so without this the date would vanish under the reader's fingers.
     The draft is pinned to the value it was typed against: once the page's value moves
     (a preset, Clear the filters), the draft no longer applies and the box shows the
     page's value again. */
  const [draft, setDraft] = useState(null)
  const { describedBy } = useFieldDescription()
  const shown = draft && draft.over === value ? draft.date : toDate(value)
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={shown}
        onChange={(d, { validationError }) => {
          if (d === null || validationError == null) {
            setDraft(null)
            onChange(d === null ? null : toIso(d))
          } else {
            setDraft({ over: value, date: d })
          }
        }}
        format="yyyy-MM-dd"
        disabled={disabled}
        // MUI's own range is 1900–2099; null keeps it rather than lifting the bound.
        minDate={toDate(minDate) ?? undefined}
        maxDate={toDate(maxDate) ?? undefined}
        views={['year', 'month', 'day']}
        openTo="day"
        // The calendar portals to <body> and takes its layer from the theme's zIndex.modal,
        // which is --z-dropdown's 400 — over a dialog, as the pickers in Select.jsx are.
        slotProps={{
          field: { clearable: isClearable },
          clearButton: { 'aria-label': 'Clear' },
          textField: {
            id,
            label,
            size: 'small',
            fullWidth: true,
            // Onto the role="group" that holds the year, month and day — what a screen
            // reader announces. The same naming as Select.jsx's nameProps. With a `label`,
            // MUI names the group from it, and nothing here may override that. The
            // enclosing Field's hint or error describes it either way.
            slotProps: {
              input: {
                ...(!label && id && { 'aria-labelledby': `${id}-label` }),
                ...(!label && { 'aria-label': placeholder }),
                'aria-describedby': describedBy,
              },
            },
          },
          actionBar: ({ pickerVariant }) => ({
            actions:
              pickerVariant === 'desktop'
                ? ['today', ...(isClearable ? ['clear'] : [])]
                : ['today', ...(isClearable ? ['clear'] : []), 'cancel', 'accept'],
          }),
        }}
      />
    </LocalizationProvider>
  )
}

/**
 * Two coupled pickers that cannot cross over each other.
 *
 * Each needs about 150px to show a whole date beside its clear and calendar buttons, so
 * the pair sits side by side where there is room and stacks, From over To, where there is
 * not — a filter column is narrower than two of them.
 */
export const DateRangeField = ({ from, to, onFrom, onTo, idFrom, idTo }) => (
  <div className="date-range">
    <DateField id={idFrom} value={from} onChange={onFrom} label="From" maxDate={to} />
    <DateField id={idTo} value={to} onChange={onTo} label="To" minDate={from} />
  </div>
)

/** Quick relative ranges, offered next to the range picker. */
export function DatePresets({ onPick }) {
  const iso = (d) => toIso(d)
  const now = new Date()
  const shift = (months) => {
    const d = new Date(now)
    d.setMonth(d.getMonth() - months)
    return d
  }
  const presets = [
    ['3m', 'Last 3 months', () => [iso(shift(3)), iso(now)]],
    ['12m', 'Last 12 months', () => [iso(shift(12)), iso(now)]],
    ['ytd', 'This year', () => [`${now.getFullYear()}-01-01`, iso(now)]],
    ['all', 'All time', () => [null, null]],
  ]
  return (
    <div className="btn-group">
      {presets.map(([key, label, fn]) => (
        <button key={key} type="button" className="btn btn-sm" onClick={() => onPick(...fn())} title={label}>
          {key === 'all' ? 'All' : key === 'ytd' ? 'YTD' : label.replace('Last ', '')}
        </button>
      ))}
    </div>
  )
}
